import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/GameConfig';
import { apiClient } from '../network/ApiClient';

/**
 * Auth Modal - Sign in with Google/Discord
 * Follows accessibility guidelines with large touch targets and readable fonts
 */
export class AuthModal {
    private scene: Phaser.Scene;
    private overlay: Phaser.GameObjects.Graphics | null = null;
    private container: Phaser.GameObjects.Container | null = null;

    // Accessible sizing constants
    private readonly MODAL_WIDTH = 500;
    private readonly MODAL_HEIGHT = 340;
    private readonly BUTTON_WIDTH = 420;
    private readonly BUTTON_HEIGHT = 56;
    private readonly TITLE_FONT_SIZE = 42;
    private readonly SUBTITLE_FONT_SIZE = 24;
    private readonly BUTTON_FONT_SIZE = 24;

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
    }

    show(): void {
        if (this.container) return;

        const { WIDTH, HEIGHT } = GAME_CONFIG;

        // Darken background - separate from modal container
        this.overlay = this.scene.add.graphics();
        this.overlay.fillStyle(0x000000, 0.7);
        this.overlay.fillRect(0, 0, WIDTH, HEIGHT);
        this.overlay.setDepth(999);
        this.overlay.setInteractive(new Phaser.Geom.Rectangle(0, 0, WIDTH, HEIGHT), Phaser.Geom.Rectangle.Contains);
        this.overlay.on('pointerdown', () => this.close());

        // Modal container
        this.container = this.scene.add.container(WIDTH / 2, HEIGHT / 2);
        this.container.setDepth(1000);

        // Modal background
        const bg = this.scene.add.graphics();
        bg.fillStyle(0x1a1a2e, 1);
        bg.fillRoundedRect(-this.MODAL_WIDTH / 2, -this.MODAL_HEIGHT / 2, this.MODAL_WIDTH, this.MODAL_HEIGHT, 12);
        bg.lineStyle(2, 0x3a3a5e, 1);
        bg.strokeRoundedRect(-this.MODAL_WIDTH / 2, -this.MODAL_HEIGHT / 2, this.MODAL_WIDTH, this.MODAL_HEIGHT, 12);
        this.container.add(bg);

        // Title - positioned higher
        const title = this.scene.add.text(0, -this.MODAL_HEIGHT / 2 + 55, 'SIGN IN', {
            fontFamily: 'monospace',
            fontSize: `${this.TITLE_FONT_SIZE}px`,
            color: '#e8e8f0',
            fontStyle: 'bold',
        });
        title.setOrigin(0.5);
        this.container.add(title);

        // Subtitle - more space below title
        const subtitle = this.scene.add.text(0, -this.MODAL_HEIGHT / 2 + 105, 'Choose a provider to continue', {
            fontFamily: 'monospace',
            fontSize: `${this.SUBTITLE_FONT_SIZE}px`,
            color: '#a8a8b8',
        });
        subtitle.setOrigin(0.5);
        this.container.add(subtitle);

        // Google button - more space from subtitle
        const googleBtn = this.createProviderButton(
            0, 10,
            'CONTINUE WITH GOOGLE',
            0xffffff,  // White background
            '#3c4043', // Google dark gray text
            0xdadce0,  // Border
            () => {
                window.location.href = apiClient.getGoogleAuthUrl();
            }
        );
        this.container.add(googleBtn);

        // Discord button
        const discordBtn = this.createProviderButton(
            0, 80,
            'CONTINUE WITH DISCORD',
            0x5865f2,  // Discord blurple
            '#ffffff', // White text
            0x5865f2,  // No visible border
            () => {
                window.location.href = apiClient.getDiscordAuthUrl();
            }
        );
        this.container.add(discordBtn);

        // Close button (X)
        const closeX = this.MODAL_WIDTH / 2 - 40;
        const closeY = -this.MODAL_HEIGHT / 2 + 40;
        const closeBtn = this.scene.add.text(closeX, closeY, 'X', {
            fontFamily: 'monospace',
            fontSize: '36px',
            color: '#606070',
            fontStyle: 'bold',
        });
        closeBtn.setOrigin(0.5);
        closeBtn.setInteractive({ useHandCursor: true });
        closeBtn.on('pointerover', () => closeBtn.setColor('#ffffff'));
        closeBtn.on('pointerout', () => closeBtn.setColor('#606070'));
        closeBtn.on('pointerdown', () => this.close());
        this.container.add(closeBtn);
    }

    close(): void {
        if (this.overlay) {
            this.overlay.destroy();
            this.overlay = null;
        }
        if (this.container) {
            this.container.destroy();
            this.container = null;
        }
    }

    private createProviderButton(
        x: number, y: number,
        text: string,
        bgColor: number,
        textColor: string,
        borderColor: number,
        callback: () => void
    ): Phaser.GameObjects.Container {
        const bg = this.scene.add.graphics();
        bg.fillStyle(bgColor, 1);
        bg.fillRoundedRect(-this.BUTTON_WIDTH / 2, -this.BUTTON_HEIGHT / 2, this.BUTTON_WIDTH, this.BUTTON_HEIGHT, 8);
        if (borderColor !== bgColor) {
            bg.lineStyle(2, borderColor, 1);
            bg.strokeRoundedRect(-this.BUTTON_WIDTH / 2, -this.BUTTON_HEIGHT / 2, this.BUTTON_WIDTH, this.BUTTON_HEIGHT, 8);
        }

        const label = this.scene.add.text(0, 0, text, {
            fontFamily: 'monospace',
            fontSize: `${this.BUTTON_FONT_SIZE}px`,
            color: textColor,
            fontStyle: 'bold',
        });
        label.setOrigin(0.5);

        const container = this.scene.add.container(x, y, [bg, label]);
        container.setSize(this.BUTTON_WIDTH, this.BUTTON_HEIGHT);
        container.setInteractive({ useHandCursor: true });

        const darkerColor = bgColor === 0xffffff ? 0xf0f0f0 :
            Phaser.Display.Color.GetColor(
                Math.floor(((bgColor >> 16) & 0xff) * 0.85),
                Math.floor(((bgColor >> 8) & 0xff) * 0.85),
                Math.floor((bgColor & 0xff) * 0.85)
            );

        container.on('pointerover', () => {
            bg.clear();
            bg.fillStyle(darkerColor, 1);
            bg.fillRoundedRect(-this.BUTTON_WIDTH / 2, -this.BUTTON_HEIGHT / 2, this.BUTTON_WIDTH, this.BUTTON_HEIGHT, 8);
            if (borderColor !== bgColor) {
                bg.lineStyle(2, borderColor, 1);
                bg.strokeRoundedRect(-this.BUTTON_WIDTH / 2, -this.BUTTON_HEIGHT / 2, this.BUTTON_WIDTH, this.BUTTON_HEIGHT, 8);
            }
        });

        container.on('pointerout', () => {
            bg.clear();
            bg.fillStyle(bgColor, 1);
            bg.fillRoundedRect(-this.BUTTON_WIDTH / 2, -this.BUTTON_HEIGHT / 2, this.BUTTON_WIDTH, this.BUTTON_HEIGHT, 8);
            if (borderColor !== bgColor) {
                bg.lineStyle(2, borderColor, 1);
                bg.strokeRoundedRect(-this.BUTTON_WIDTH / 2, -this.BUTTON_HEIGHT / 2, this.BUTTON_WIDTH, this.BUTTON_HEIGHT, 8);
            }
        });

        container.on('pointerdown', callback);

        return container;
    }
}
